import { useCallback, useEffect, useRef, useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material"
import PenIcon from "@mui/icons-material/CreateOutlined"
import ArrowIcon from "@mui/icons-material/NorthEastOutlined"
import RectIcon from "@mui/icons-material/CropSquareOutlined"
import RedactIcon from "@mui/icons-material/FormatColorFillOutlined"
import CropIcon from "@mui/icons-material/CropOutlined"
import RotateIcon from "@mui/icons-material/Rotate90DegreesCwOutlined"
import UndoIcon from "@mui/icons-material/UndoOutlined"

import {
  canvasToFile,
  cropCanvas,
  drawShape,
  exportMimeFor,
  fitWorkingSize,
  getCanvasPoint,
  loadImageFromFile,
  rotateCanvas,
} from "./imageEdit"

/** Long edge the working canvas is scaled to. See fitWorkingSize for why. */
const WORKING_EDGE = 1600

const TOOLS = [
  { value: "pen", label: "Draw", Icon: PenIcon },
  { value: "arrow", label: "Arrow", Icon: ArrowIcon },
  { value: "rect", label: "Box", Icon: RectIcon },
  { value: "redact", label: "Redact", Icon: RedactIcon },
  { value: "crop", label: "Crop", Icon: CropIcon },
]

const COLORS = ["#e04b3c", "#e9a13b", "#12b886", "#2f86e0", "#10230a"]

/**
 * Mark up an image before it is sent.
 *
 * ── Why it happens before the upload, not after ─────────────────────────────
 *
 * The edited image is the one that leaves the machine. An editor that uploads
 * the original and stores edits alongside it has published the thing somebody
 * was trying to cover up — which for the redaction tool is the entire point.
 * The original never reaches the server.
 *
 * ── Why the history is a stack of canvases ──────────────────────────────────
 *
 * Every operation produces a new canvas and pushes the old one. Undo is then a
 * pop rather than an attempt to reverse a destructive pixel operation — you
 * cannot un-draw a stroke from a bitmap, and a system that pretends otherwise
 * quietly loses whatever was underneath.
 *
 * The cost is memory: a few full-size canvases. Bounded by the working
 * resolution above, which is the other reason that scaling exists.
 */
export default function ImageEditorDialog({ open, file, onCancel, onSave }) {
  const canvasRef = useRef(null)
  const historyRef = useRef([])
  const dragRef = useRef(null)

  const [tool, setTool] = useState("pen")
  const [color, setColor] = useState(COLORS[0])
  const [cropRect, setCropRect] = useState(null)
  const [busy, setBusy] = useState(false)
  const [ready, setReady] = useState(false)
  const [canUndo, setCanUndo] = useState(false)

  /** Paint `source` into the visible canvas, sizing it to match. */
  const show = useCallback((source) => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = source.width
    canvas.height = source.height
    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(source, 0, 0)
  }, [])

  const snapshot = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const copy = document.createElement("canvas")
    copy.width = canvas.width
    copy.height = canvas.height
    copy.getContext("2d").drawImage(canvas, 0, 0)
    historyRef.current.push(copy)
    setCanUndo(true)
  }, [])

  // Load the file in, scaled to the working size.
  useEffect(() => {
    if (!open || !file) return undefined
    let cancelled = false
    setReady(false)
    historyRef.current = []
    setCanUndo(false)
    setCropRect(null)

    loadImageFromFile(file)
      .then(({ img, url }) => {
        if (cancelled) return URL.revokeObjectURL(url)
        const { width, height } = fitWorkingSize(img.naturalWidth, img.naturalHeight, WORKING_EDGE)
        const base = document.createElement("canvas")
        base.width = width
        base.height = height
        base.getContext("2d").drawImage(img, 0, 0, width, height)
        URL.revokeObjectURL(url)
        show(base)
        setReady(true)
      })
      .catch(() => setReady(false))

    return () => {
      cancelled = true
    }
  }, [open, file, show])

  const undo = () => {
    const previous = historyRef.current.pop()
    if (!previous) return
    show(previous)
    setCanUndo(historyRef.current.length > 0)
    setCropRect(null)
  }

  const rotate = () => {
    snapshot()
    show(rotateCanvas(canvasRef.current, 1))
    setCropRect(null)
  }

  const applyCrop = () => {
    if (!cropRect) return
    snapshot()
    show(cropCanvas(canvasRef.current, cropRect))
    setCropRect(null)
    setTool("pen")
  }

  const onPointerDown = (event) => {
    if (!ready) return
    const canvas = canvasRef.current
    canvas.setPointerCapture(event.pointerId)
    const point = getCanvasPoint(canvas, event.clientX, event.clientY)

    if (tool === "crop") {
      dragRef.current = { start: point, end: point }
      setCropRect({ start: point, end: point })
      return
    }

    snapshot()
    dragRef.current = { start: point, end: point }

    if (tool === "pen") {
      const ctx = canvas.getContext("2d")
      ctx.save()
      ctx.strokeStyle = color
      ctx.lineWidth = strokeWidth(canvas)
      ctx.lineCap = "round"
      ctx.lineJoin = "round"
      ctx.beginPath()
      ctx.moveTo(point.x, point.y)
    }
  }

  const onPointerMove = (event) => {
    const drag = dragRef.current
    if (!drag) return
    const canvas = canvasRef.current
    const point = getCanvasPoint(canvas, event.clientX, event.clientY)
    drag.end = point

    if (tool === "crop") {
      setCropRect({ start: drag.start, end: point })
      return
    }

    if (tool === "pen") {
      const ctx = canvas.getContext("2d")
      ctx.lineTo(point.x, point.y)
      ctx.stroke()
      return
    }

    /*
     * A shape is previewed by restoring the snapshot and redrawing.
     *
     * Drawing straight onto the canvas on every move would leave a smear of
     * every intermediate rectangle — the bitmap has no concept of "the shape I
     * drew last frame" to erase.
     */
    const base = historyRef.current[historyRef.current.length - 1]
    if (base) show(base)
    drawShape(canvas.getContext("2d"), tool, drag.start, point, color, strokeWidth(canvas))
  }

  const onPointerUp = () => {
    if (tool === "pen" && dragRef.current) canvasRef.current?.getContext("2d").restore()
    dragRef.current = null
  }

  const save = async () => {
    setBusy(true)
    try {
      const mime = exportMimeFor(file.type)
      const name = renameFor(file.name, mime)
      onSave(await canvasToFile(canvasRef.current, mime, name))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit image
        <Typography variant="caption" color="text.secondary" display="block">
          {file?.name} — only the edited version is sent.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "action.hover" }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <ToggleButtonGroup exclusive size="small" value={tool} onChange={(_e, value) => value && setTool(value)}>
            {TOOLS.map(({ value, label, Icon }) => (
              <ToggleButton key={value} value={value} aria-label={label}>
                <Tooltip title={label}>
                  <Icon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Redaction has no colour: it is always opaque near-black, because a
              translucent or brightly coloured one invites somebody to treat it
              as a highlight. */}
          {tool !== "redact" && tool !== "crop" ? (
            <Stack direction="row" spacing={0.5}>
              {COLORS.map((swatch) => (
                <Box
                  key={swatch}
                  onClick={() => setColor(swatch)}
                  role="button"
                  aria-label={`Use ${swatch}`}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: swatch,
                    cursor: "pointer",
                    outline: color === swatch ? "2px solid" : "1px solid",
                    outlineColor: color === swatch ? "primary.dark" : "divider",
                    outlineOffset: 2,
                  }}
                />
              ))}
            </Stack>
          ) : null}

          <Box sx={{ flex: 1 }} />

          {tool === "crop" && cropRect ? (
            <Button size="small" variant="contained" onClick={applyCrop}>
              Apply crop
            </Button>
          ) : null}
          <Tooltip title="Rotate a quarter turn">
            <Button size="small" startIcon={<RotateIcon />} onClick={rotate} disabled={!ready}>
              Rotate
            </Button>
          </Tooltip>
          <Tooltip title="Undo the last change">
            <span>
              <Button size="small" startIcon={<UndoIcon />} onClick={undo} disabled={!canUndo}>
                Undo
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <Box sx={{ position: "relative", display: "grid", placeItems: "center" }}>
          <Box
            component="canvas"
            ref={canvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            sx={{
              maxWidth: "100%",
              maxHeight: "58vh",
              borderRadius: 1.5,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              cursor: tool === "crop" ? "crosshair" : "cell",
              // The browser's own pan/zoom would fight every drag on a touch
              // screen, and the editor would be unusable on a tablet.
              touchAction: "none",
            }}
          />
          {tool === "crop" && cropRect ? <CropOverlay canvas={canvasRef.current} rect={cropRect} /> : null}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={busy || !ready}>
          {busy ? "Saving…" : "Use this image"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Stroke width relative to the image, so a line looks the same on any size. */
function strokeWidth(canvas) {
  return Math.max(2, Math.round(Math.max(canvas.width, canvas.height) / 220))
}

/** `photo.heic` exported as PNG has to stop claiming to be a HEIC. */
function renameFor(name, mime) {
  const extension = mime === "image/jpeg" ? "jpg" : mime === "image/webp" ? "webp" : "png"
  return `${String(name).replace(/\.[^.]+$/, "")}.${extension}`
}

/**
 * The crop rectangle, drawn over the canvas rather than onto it.
 *
 * Onto it would mean the marching-ants outline ends up in the exported image,
 * which is the kind of bug nobody finds until a client asks what the dashed box
 * on their invoice is.
 */
function CropOverlay({ canvas, rect }) {
  if (!canvas) return null
  const bounds = canvas.getBoundingClientRect()
  const scaleX = bounds.width / canvas.width
  const scaleY = bounds.height / canvas.height
  const left = Math.min(rect.start.x, rect.end.x) * scaleX
  const top = Math.min(rect.start.y, rect.end.y) * scaleY
  const width = Math.abs(rect.end.x - rect.start.x) * scaleX
  const height = Math.abs(rect.end.y - rect.start.y) * scaleY

  return (
    <Box
      sx={{
        position: "absolute",
        left: `calc(50% - ${bounds.width / 2}px + ${left}px)`,
        top,
        width,
        height,
        border: "2px dashed",
        borderColor: "primary.dark",
        boxShadow: "0 0 0 9999px rgba(16,35,10,0.35)",
        pointerEvents: "none",
      }}
    />
  )
}
